import { useState } from 'react';
import PropTypes from 'prop-types';
import { firstBy } from 'thenby';
import { Box, Divider, Group, Stack } from '@mantine/core';

import useReduxPopupSystem from 'src/new_arch/hooks/useReduxPopupSystem';

import WhiteBox from '../WhiteBox/WhiteBox';

import './WorkspaceMemberList.scss';
import WorkspaceMember from '../../entities/workspaceMember';
import WorkspaceInvitation from '../../entities/workspaceInvitation';
import WorkspaceMemberWithRoleContainer from '../../containers/WorkspaceMemberWithRoleContainer/WorkspaceMemberWithRoleContainer';
import WorkspaceInvitationContainer from '../../containers/WorkspaceInvitationContainer/WorkspaceInvitationContainer';
import Button from '../../new_arch/components/Button/Button';
import InviteWorkspaceMemberModal from '../../new_arch/modules/workspaces/modals/InviteWorkspaceMemberModal/InviteWorkspaceMemberModal';
import MembersUsageContainer from '../../containers/MembersUsageContainer/MembersUsageContainer';
import Workspace from '../../entities/workspace';
import PricingSubscriptionSelectionModal from '../../new_arch/modules/workspaces/modals/PricingSubscriptionSelectionModal/PricingSubscriptionSelectionModal';
import PricingSubscriptionPresenter from '../../presenters/PlanSettings/PricingSubscriptionPresenter';
import WorkspaceSubscriptionCheckoutModalContainer from '../../containers/WorkspaceSubscriptionCheckoutModalContainer/WorkspaceSubscriptionCheckoutModalContainer';
import openEmailClient from '../../services/openEmailClient';
import { SUPPORT_EMAIL } from '../../config/email-addresses';
import NonMembersSection from '../../new_arch/modules/workspaces/components/NonMembersSection/NonMembersSection';
import Text from '../../new_arch/modules/ui/components/Text/Text';

const WorkspaceMemberList = ( {
	workspaceID, members, pendingInvites,
	showDialogWithIcon, popupInfo, workspace, onMemberAdded
} ) => {
	const [ showInviteModal, setShowInviteModal ] = useState( false );
	const [ suscriptionModalIsOpen, setSuscriptionModalIsOpen ] = useState( false );
	const [ showingCheckoutModal, setShowingCheckoutModal ] = useState( false );
	const [ pricingPlanPrice, setPricingPlanPrice ] = useState( undefined );
	const popupSystem = useReduxPopupSystem();

	const isOwner = workspace.currentUserIsOwner;

	const sortedMembers = members.sort(
		firstBy( member => member.role?.level )
			.thenBy( member => member.user.name, { ignoreCase: true } )
	);
	const sortedPendingInvites = pendingInvites.sort(
		firstBy( invite => invite.role?.level )
			.thenBy( 'email' )
	);

	const buttonActions = {
		'show-pricing-plans': () => setSuscriptionModalIsOpen( true ),
		'contact-us': () => openEmailClient( SUPPORT_EMAIL )
	};

	const showErrorModal = () => showDialogWithIcon(
		popupInfo.title,
		popupInfo.text,
		popupInfo.buttonText,
		true
	).then( accept => accept && buttonActions[ popupInfo.buttonAction ]() );

	const onInviteMemberClick = () => {
		if ( workspace.isOnFreeTrial && !workspace.subscription?.inPlanTrial ) {
			showErrorModal();
			return;
		}

		if ( workspace.hasReachedMembersLimit ) {
			const { planName, membersAddOnsLimit } = workspace.subscription;
			popupSystem.showDefaultConfirmationDialog( {
				title: 'Upgrade to Studio Plan for Unlimited seats',
				description: `On your current plan (${planName}) you can only add ${membersAddOnsLimit} extra member${membersAddOnsLimit > 1 ? 's' : ''}, upgrade to a Studio Plan to add as many members as you want.`,
				acceptText: 'See Plans',
				cancelText: 'Cancel',
				acceptButtonColor: 'primary'
			} ).then( ( confirmed ) => {
				popupSystem.hideConfirmationDialog();

				if ( confirmed ) {
					setSuscriptionModalIsOpen( true );
				}
			} );
			return;
		}

		setShowInviteModal( true );
	};

	const inviteMemberButton = (
		<Button
			onClick={() => onInviteMemberClick()}
			disabled={!workspace.isSubscriptionActive}
		>
			Invite Members
		</Button>
	);

	const onUpgradeButtonClicked = ( aPricingPlanPrice ) => {
		setPricingPlanPrice( aPricingPlanPrice );
		setShowingCheckoutModal( true );
		setSuscriptionModalIsOpen( false );
	};

	const closeCheckoutModal = () => setShowingCheckoutModal( false );

	const onSuccessfulPaymentDialogAccepted = () => {
		setSuscriptionModalIsOpen( false );
		setShowingCheckoutModal( false );
	};

	const pricingSubscriptionPresenter = new PricingSubscriptionPresenter( { workspace } );

	const renderWorkspaceSubscriptionCheckoutModal = () => (
		<WorkspaceSubscriptionCheckoutModalContainer
			workspaceID={workspace.id}
			onClose={closeCheckoutModal}
			onSuccessfulDialogAccepted={onSuccessfulPaymentDialogAccepted}
			pricingPlanPrice={pricingPlanPrice}
		/>
	);

	const renderPricingSubscriptionSelectionModal = () => (
		<PricingSubscriptionSelectionModal
			workspaceID={workspace.id}
			presenter={pricingSubscriptionPresenter}
			onClose={() => setSuscriptionModalIsOpen( false )}
			onUpgradeButtonClicked={onUpgradeButtonClicked}
		/>
	);

	return (
		<div className="WorkspaceMemberList" style={{ maxHeight: 'calc(100vh - 160px)', overflowY: 'auto' }}>
			{isOwner && suscriptionModalIsOpen && renderPricingSubscriptionSelectionModal() }
			{isOwner && showingCheckoutModal && renderWorkspaceSubscriptionCheckoutModal() }
			{isOwner && showInviteModal
				&& (
					<InviteWorkspaceMemberModal
						workspace={workspace}
						onClose={() => setShowInviteModal( false )}
						onSuccess={onMemberAdded}
					/>
				)}
			<Stack spacing={16} style={{ width: '100%' }}>
				<WhiteBox>
					<div className="title">
						<h4>{workspace.name}{"'s Members"}</h4>
						{isOwner && (
							<p style={{ margin: 0, marginTop: 4, fontFamily: 'Open Sans', fontSize: 14, fontWeight: 400, fontStyle: 'normal', color: 'var(--color-text-secondary)', lineHeight: '22px', letterSpacing: 0 }}>
								{'Give your team the ability to create and manage projects, and have full management & sharing permissions in projects. '}
								<a href="https://help.verybusy.io" target="_blank" rel="noreferrer" style={{ fontFamily: 'Open Sans', fontSize: 14, fontWeight: 400, fontStyle: 'normal', lineHeight: '22px', letterSpacing: 0, color: '#228be6', textDecoration: 'underline', textDecorationStyle: 'solid', textUnderlineOffset: 0, textDecorationSkipInk: 'auto' }}>Learn More about role and permissions.</a>
							</p>
						)}
					</div>
					{isOwner && ( workspace.hasAPaidPlan || workspace.subscription?.inPlanTrial ) && (
						<MembersUsageContainer
							workspace={workspace}
							inviteMemberButton={inviteMemberButton}
							onPaidFeatureModalAcceptClick={() => setSuscriptionModalIsOpen( true )}
						/>
					)}
					<Group spacing={2} w="100%" className="column-names" position="apart" noWrap>
						<Box style={{ flex: 1 }}>
							Members
						</Box>
						<Box w="120px">
							Projects
						</Box>
						<Box w="120px">
							Workspace Role
						</Box>
						<Box w="40px" />
					</Group>
					<Divider color="gray.2" w="100%" h="1px" mt={10} mb={12} />
					<Stack spacing={16} className="members-invites">
						{sortedMembers.map( member => (
							<WorkspaceMemberWithRoleContainer
								key={member.id}
								workspaceID={workspaceID}
								member={member}
							/>
						) )}
						{pendingInvites.length > 0
							&& sortedPendingInvites.map( invite => (
								<WorkspaceInvitationContainer
									key={invite.id}
									workspaceID={workspaceID}
									invite={invite}
								/>
							) )}
					</Stack>
					{isOwner && !workspace.hasAPaidPlan && !workspace.subscription?.inPlanTrial
					&& (
						<div className="button-container">
							<Button onClick={onInviteMemberClick}>
								Invite Members
							</Button>
						</div>
					)}
				</WhiteBox>
				<NonMembersSection
					workspaceID={workspaceID}
					isOwner={isOwner}
					onMemberAdded={onMemberAdded}
				/>
			</Stack>
			{isOwner && (
				<Text
					variant="body"
					pl={32}
					mt={-6}
					style={{ fontSize: 12, fontWeight: 400, lineHeight: '18px', letterSpacing: '0.13px', color: 'var(--color-text-secondary)' }}
				>
					Seats impact in billing,{' '}
					<span style={{ fontWeight: 600, textDecoration: 'underline', color: 'var(--color-text-primary)' }}>manage seats</span>
				</Text>
			)}
		</div>
	);
};

WorkspaceMemberList.propTypes = {
	workspaceID: PropTypes.number,
	members: PropTypes.arrayOf( PropTypes.instanceOf( WorkspaceMember ) ),
	pendingInvites: PropTypes.arrayOf( PropTypes.instanceOf( WorkspaceInvitation ) ),
	showDialogWithIcon: PropTypes.func,
	popupInfo: PropTypes.shape( {
		title: PropTypes.string.isRequired,
		text: PropTypes.string.isRequired,
		buttonText: PropTypes.string.isRequired,
		buttonAction: PropTypes.oneOf( [ 'show-pricing-plans', 'contact-us' ] ).isRequired
	} ),
	workspace: PropTypes.instanceOf( Workspace ).isRequired,
	onMemberAdded: PropTypes.func
};

WorkspaceMemberList.defaultProps = {
	workspaceID: undefined,
	members: [],
	pendingInvites: [],
	showDialogWithIcon: () => {},
	popupInfo: {},
	onMemberAdded: () => {}
};

export default WorkspaceMemberList;
